import { useEffect, useMemo, useState, type ChangeEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { DiktatFeld, VorschlagsKasten } from '@symphenia/ui-components'
import { tagebuchInhalt, tagebuchMitDiktat, type SiteDocAiProposal, type SiteDocAiProposalItem, type SiteDocDetail, type TagebuchDiktatEintrag } from '@symphenia/shared-types'
import { fehlertext } from '@/lib/fehlertext'
import { useFeld, useSpeicherstelle, type Datensatz, type Speicherstelle } from '@/lib/speichern'
import { siteDocAiProposal, updateSiteDoc } from '../../api/site-docs.api'
import { tagebuchTeilLabels } from './tagebuch-teile'

type Eintrag = SiteDocAiProposalItem<TagebuchDiktatEintrag>

/** Online-Zustand des Browsers — der Knopf braucht Netz (AP-46 21.1). */
function useOnline(): boolean {
  const [online, setOnline] = useState(() => navigator.onLine)
  useEffect(() => {
    const an = () => setOnline(true)
    const aus = () => setOnline(false)
    window.addEventListener('online', an)
    window.addEventListener('offline', aus)
    return () => {
      window.removeEventListener('online', an)
      window.removeEventListener('offline', aus)
    }
  }, [])
  return online
}

/**
 * AP-48 13. im ERP — **Diktat in Abschnitte** am Entwurf des Bautagesberichts: Feld „Was wurde gemacht?"
 * (`dictation` am Entwurf), „Vorschlag aus dem Text" und der Vorschlagskasten. Übernommenes kommt hinter das
 * Vorhandene (`tagebuchMitDiktat`); eigene Belegschaft, Stunden und Temperatur fasst das Diktat nie an
 * (TB-15). Die Art einer Behinderung bleibt markiert, bis der Erfasser sie bestätigt.
 */
export function TagebuchDiktat({ dok, stelle: inhaltStelle }: { dok: SiteDocDetail; stelle: Speicherstelle<unknown> }) {
  const { t } = useTranslation()
  const online = useOnline()
  const daten = useMemo<Datensatz>(() => ({ dictation: dok.dictation ?? '' }), [dok.dictation])
  const stelle = useSpeicherstelle<Datensatz>({
    schluessel: `tagebuchDiktat:${dok.id}`,
    bezeichnung: t('siteDocs.diktat.titel'),
    daten,
    schreibe: async (patch) => {
      const neu = await updateSiteDoc('DIARY', dok.id, { dictation: (patch.dictation as string | null) || null })
      return { dictation: neu.dictation ?? '' }
    },
  }) as Speicherstelle<unknown>
  const feld = useFeld<string | null>(stelle, 'dictation', { label: t('siteDocs.diktat.titel') })
  const [vorschlag, setVorschlag] = useState<SiteDocAiProposal<TagebuchDiktatEintrag> | null>(null)
  const [fehler, setFehler] = useState<string | null>(null)

  const holen = async () => {
    setFehler(null)
    try {
      setVorschlag((await siteDocAiProposal({ kind: 'DIARY', text: feld.props.value, projectId: dok.projectId, projectRefId: dok.projectRefId, documentDate: dok.documentDate.slice(0, 10) })) as SiteDocAiProposal<TagebuchDiktatEintrag>)
    } catch (e) {
      setFehler(fehlertext(e, t))
    }
  }

  const uebernehmen = async (liste: Eintrag[]) => {
    setFehler(null)
    const gewaehlt = liste.map((e) => e.daten)
    const e = await inhaltStelle.sende({
      kennung: `diktat#${Date.now()}`,
      felder: ['wetter', 'belegschaft', 'geraete', 'leistungen', 'lieferungen', 'behinderungen', 'anordnungen', 'vorkommnisse'],
      labels: tagebuchTeilLabels(t),
      operation: (aktuell) => {
        const n = tagebuchMitDiktat(tagebuchInhalt(aktuell), gewaehlt, () => crypto.randomUUID())
        return { wetter: n.wetter, belegschaft: n.belegschaft, geraete: n.geraete, leistungen: n.leistungen, lieferungen: n.lieferungen, behinderungen: n.behinderungen, anordnungen: n.anordnungen, vorkommnisse: n.vorkommnisse }
      },
    })
    if (e.art === 'fehler') {
      setFehler(e.text)
      throw new Error(e.text)
    }
  }

  const darstellung = (e: Eintrag) => {
    const d = e.daten
    const was = t(`siteDocs.diary.dictation.${d.abschnitt}`)
    let text: string
    switch (d.abschnitt) {
      case 'leistung':
      case 'vorkommnis':
        text = d.text
        break
      case 'lieferung':
        text = [d.text, d.lieferant, d.lieferschein].filter(Boolean).join(' · ')
        break
      case 'geraet':
        text = `${d.anzahl} × ${d.bezeichnung}${d.stunden !== null ? ` · ${String(d.stunden).replace('.', ',')} h` : ''}${d.artikelnummer ? ` · ${d.artikelnummer}` : ''}`
        break
      case 'fremd':
        text = `${d.firma} · ${d.anzahl}${d.taetigkeit ? ` · ${d.taetigkeit}` : ''}`
        break
      case 'behinderung':
        text = `${t(`siteDocs.diary.hindranceKinds.${d.art}`)}: ${d.text}`
        break
      case 'anordnung':
        text = `${d.von}: ${d.text}`
        break
      case 'wetter':
        text = t(`siteDocs.diary.weather.${d.lage}`)
        break
    }
    return (
      <span>
        <span className="font-medium">{was}:</span> {text}
      </span>
    )
  }

  return (
    <div className="space-y-3 rounded-md border border-border p-3">
      <DiktatFeld
        wert={feld.props.value}
        onChange={(v) => feld.props.onChange({ target: { value: v } } as ChangeEvent<HTMLTextAreaElement>)}
        onBlur={feld.props.onBlur}
        onVorschlag={holen}
        online={online}
        fehler={fehler}
        texte={{ titel: t('siteDocs.diktat.titel'), tastatur: t('siteDocs.diktat.tastatur'), knopf: t('siteDocs.diktat.knopf'), arbeitet: t('siteDocs.diktat.arbeitet'), ohneNetz: t('siteDocs.diktat.ohneNetz'), kiHinweis: t('siteDocs.diktat.kiHinweis') }}
      />
      {vorschlag && (
        <>
          {!!vorschlag.hinweise?.length && (
            <ul className="list-disc space-y-0.5 pl-5 text-sm text-amber-800 dark:text-amber-200">
              {vorschlag.hinweise.map((h) => (
                <li key={h}>{h}</li>
              ))}
            </ul>
          )}
          <VorschlagsKasten
            eintraege={vorschlag.items}
            darstellung={darstellung}
            onUebernehmen={uebernehmen}
            onVerwerfen={() => setVorschlag(null)}
            texte={{
              titel: t('siteDocs.diktat.vorschlag'),
              uebernehmen: t('siteDocs.diktat.uebernehmen'),
              markierte: t('siteDocs.diktat.markierte'),
              verwerfen: t('siteDocs.diktat.verwerfen'),
              leer: t('siteDocs.diktat.leer'),
              hinweis: (c) => t(`siteDocs.diktat.hints.${c}`, c),
            }}
          />
        </>
      )}
    </div>
  )
}
